import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

const GAP = 8

interface HoverCardProps {
  /** What the card shows. Rendered only while the trigger is hovered or focused. */
  content: React.ReactNode
  children: React.ReactNode
  className?: string
}

/**
 * A rich hover/focus card, for content too big for `Tooltip`'s one-line bubble.
 *
 * Portalled and `fixed` because its triggers sit inside a scrolling dialog body,
 * which would clip an absolutely positioned card at the body's edge. It opens
 * below the trigger, flips above when there is no room, and closes on scroll
 * rather than drifting away from what it describes.
 */
export function HoverCard({ content, children, className }: HoverCardProps) {
  const id = `hovercard-${useId().replace(/:/g, '')}`
  const triggerRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  const show = () => {
    if (triggerRef.current) setAnchor(triggerRef.current.getBoundingClientRect())
  }
  const hide = () => {
    setAnchor(null)
    setPos(null)
  }

  // Placed after the first paint off-screen, once the card's own size is known.
  useLayoutEffect(() => {
    if (!anchor || !cardRef.current) return
    const { width, height } = cardRef.current.getBoundingClientRect()
    const fitsBelow = anchor.bottom + GAP + height <= window.innerHeight
    setPos({
      top: fitsBelow ? anchor.bottom + GAP : Math.max(GAP, anchor.top - GAP - height),
      left: Math.min(Math.max(GAP, anchor.left), window.innerWidth - width - GAP),
    })
  }, [anchor])

  useEffect(() => {
    if (!anchor) return
    window.addEventListener('scroll', hide, true)
    return () => window.removeEventListener('scroll', hide, true)
  }, [anchor])

  return (
    <div
      ref={triggerRef}
      tabIndex={0}
      aria-describedby={anchor ? id : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      className={cn(
        'focus-visible:ring-primary/40 rounded-lg outline-none focus-visible:ring-2',
        className,
      )}
    >
      {children}
      {anchor &&
        createPortal(
          <div
            ref={cardRef}
            id={id}
            role="tooltip"
            style={pos ?? { top: -9999, left: -9999 }}
            className="border-line bg-surface-3 text-fg pointer-events-none fixed z-[60] w-80 rounded-lg border p-3 text-xs shadow-xl"
          >
            {content}
          </div>,
          document.body,
        )}
    </div>
  )
}
